import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Grid,
    Typography,
    TextField,
    Autocomplete,
    FormControlLabel,
    InputAdornment
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.css';
import TrimmedTextField from '../../../../components/TrimmedTextField';
import LoadingSwitch from '../../../../components/LoadingSwitch';
import { MODAL_BORDER_RADIUS } from '../../utils/dateUtils';

const AddItemModal = ({
    open,
    onClose,
    onAdd,
    onPreview,
    formState,
    handleFormChange,
    handleContentTypeChange,
    handleContentSelection,
    handleCollectionSelection,
    handleContentPackSelection,
    handleBatchSendChange,
    simulationData,
    courseData,
    collectionData,
    contentPackData,
    featureSwitchesApiData,
    style
}) => {
    const [simulationType, setSimulationType] = useState(null);
    const [courseType, setCourseType] = useState(null);
    const [isWeekendPauseLoading, setIsWeekendPauseLoading] = useState(false);

    // Refs for flatpickr
    const endDateRef = useRef(null);
    const startTimeRef = useRef(null);
    const endTimeRef = useRef(null);
    const endDatePickerRef = useRef(null);
    const startTimePickerRef = useRef(null);
    const endTimePickerRef = useRef(null);

    const simulationsEnabled = featureSwitchesApiData?.enableJourneyBuilderSimulations !== false;
    const coursesEnabled = featureSwitchesApiData?.enableJourneyBuilderCourses !== false;
    const contentPacksEnabled = featureSwitchesApiData?.enableContentPacks !== false;
    const noneEnabled = !simulationsEnabled && !coursesEnabled;

    // Date picker config
    const dateConfig = {
        enableTime: false,
        dateFormat: "Y-m-d",
        allowInput: true
    };

    // Time picker config
    const timeConfig = {
        enableTime: true,
        noCalendar: true,
        dateFormat: "H:i",
        time_24hr: true,
        minuteIncrement: 1
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return dateStr;
    };

    const handleWeekendPauseChange = async () => {
        setIsWeekendPauseLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 300));
            handleBatchSendChange('pauseWeekends', !formState.pauseWeekends);
        } finally {
            setIsWeekendPauseLoading(false);
        }
    };

    // Initialize flatpickr instances when batch send is enabled
    useEffect(() => {
        if (!open || !formState.isBatchSend) return;

        // Small delay to ensure DOM is ready
        const timer = setTimeout(() => {
            // End date picker
            if (endDateRef.current && !endDatePickerRef.current) {
                endDatePickerRef.current = flatpickr(endDateRef.current, {
                    ...dateConfig,
                    defaultDate: formState.scheduledEndDate || undefined,
                    minDate: formState.scheduledFor || undefined,
                    onChange: ([date]) => {
                        if (date) {
                            handleBatchSendChange('scheduledEndDate', flatpickr.formatDate(date, "Y-m-d"));
                        }
                    }
                });
            }

            // Start time picker
            if (startTimeRef.current && !startTimePickerRef.current) {
                startTimePickerRef.current = flatpickr(startTimeRef.current, {
                    ...timeConfig,
                    defaultDate: formState.sendingTimeStart || "09:00",
                    onClose: (selectedDates, dateStr) => {
                        if (dateStr) {
                            handleBatchSendChange('sendingTimeStart', dateStr);
                        }
                    }
                });
            }

            // End time picker
            if (endTimeRef.current && !endTimePickerRef.current) {
                endTimePickerRef.current = flatpickr(endTimeRef.current, {
                    ...timeConfig,
                    defaultDate: formState.sendingTimeEnd || "17:00",
                    onClose: (selectedDates, dateStr) => {
                        if (dateStr) {
                            handleBatchSendChange('sendingTimeEnd', dateStr);
                        }
                    }
                });
            }
        }, 50);

        return () => {
            clearTimeout(timer);
        };
    }, [open, formState.isBatchSend]);

    // Cleanup flatpickr instances when modal closes or batch send is disabled
    useEffect(() => {
        if (!open || !formState.isBatchSend) {
            if (endDatePickerRef.current) {
                endDatePickerRef.current.destroy();
                endDatePickerRef.current = null;
            }
            if (startTimePickerRef.current) {
                startTimePickerRef.current.destroy();
                startTimePickerRef.current = null;
            }
            if (endTimePickerRef.current) {
                endTimePickerRef.current.destroy();
                endTimePickerRef.current = null;
            }
        }
    }, [open, formState.isBatchSend]);

    // Update picker values when formState changes
    useEffect(() => {
        if (endDatePickerRef.current && formState.scheduledEndDate) {
            endDatePickerRef.current.setDate(formState.scheduledEndDate);
        }
    }, [formState.scheduledEndDate]);

    useEffect(() => {
        if (startTimePickerRef.current && formState.sendingTimeStart) {
            startTimePickerRef.current.setDate(formState.sendingTimeStart);
        }
    }, [formState.sendingTimeStart]);

    useEffect(() => {
        if (endTimePickerRef.current && formState.sendingTimeEnd) {
            endTimePickerRef.current.setDate(formState.sendingTimeEnd);
        }
    }, [formState.sendingTimeEnd]);

    useEffect(() => {
        if (!open) {
            setSimulationType(null);
            setCourseType(null);
            return;
        }

        // Set default content type based on what's enabled
        if (simulationsEnabled && formState.contentType !== 'Simulation' && formState.contentType !== 'Course') {
            const syntheticEvent = {
                target: { value: 'Simulation' }
            };
            handleContentTypeChange(syntheticEvent);
        }
    }, [open, simulationsEnabled]);

    useEffect(() => {
        if (!open) {
            setSimulationType(null);
            setCourseType(null);
            return;
        }

        // Handle simulation subtype detection
        if (formState.contentType === 'Simulation') {
            if (simulationType === null) {
                if (formState.selectedContentName === 'Random Simulation' || formState.isRandomSimulation) {
                    setSimulationType('Random');
                } else if (formState.selectedContentName && formState.selectedContentName !== 'Random Simulation') {
                    setSimulationType('Manual');
                }
            }
        } else if (formState.contentType === 'Course') {
            setSimulationType(null);
            // Handle course subtype detection
            if (courseType === null) {
                if (formState.selectedContentName === 'Content Pack' || formState.isContentPack) {
                    setCourseType('ContentPack');
                } else if (formState.selectedContentName && formState.selectedContentName !== 'Content Pack') {
                    setCourseType('Individual');
                }
            }
        }
    }, [open, formState.contentType, formState.selectedContentName, formState.isRandomSimulation, formState.isContentPack]);

    const handleContentTypeButtonClick = (contentType) => {
        if (contentType === 'Course' && !coursesEnabled) return;
        if (contentType === 'Simulation' && !simulationsEnabled) return;

        setSimulationType(null);
        setCourseType(null);
        const syntheticEvent = {
            target: { value: contentType }
        };
        handleContentTypeChange(syntheticEvent);
    };

    const handleSimulationTypeClick = (type) => {
        setSimulationType(type);
        if (type === 'Random') {
            const syntheticEvent = {
                target: { value: 'Random Simulation' }
            };
            handleContentSelection(syntheticEvent);
        } else {
            const resetEvent = {
                target: { value: '' }
            };
            handleContentSelection(resetEvent);
        }
    };

    const handleCourseTypeClick = (type) => {
        setCourseType(type);
        if (type === 'ContentPack') {
            const syntheticEvent = {
                target: { value: 'Content Pack' }
            };
            handleContentSelection(syntheticEvent);
        } else {
            const resetEvent = {
                target: { value: '' }
            };
            handleContentSelection(resetEvent);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: MODAL_BORDER_RADIUS,
                    ...style
                },
                sx: { bgcolor: 'background.default' }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Add Item</Typography>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        disabled={!formState.contentId}
                        onClick={onPreview}
                        startIcon={<VisibilityIcon />}
                    >
                        Preview
                    </Button>
                </Box>
            </DialogTitle>
            <DialogContent>
                {noneEnabled ? (
                    <Box sx={{ py: 3 }}>
                        <Typography variant="body1" color="text.secondary" align="center">
                            No content types are enabled for Journey Builder. Please contact your administrator.
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {/* Content Type Selection Buttons */}
                        <Grid item xs={12} sx={{ mb: -0.1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Content type
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                                <Button
                                    variant={formState.contentType === 'Course' ? 'contained' : 'outlined'}
                                    color={formState.contentType === 'Course' ? 'warning' : 'inherit'}
                                    onClick={() => handleContentTypeButtonClick('Course')}
                                    disabled={!coursesEnabled}
                                    sx={{ flex: 1 }}
                                >
                                    Course
                                </Button>
                                <Button
                                    variant={formState.contentType === 'Simulation' ? 'contained' : 'outlined'}
                                    color={formState.contentType === 'Simulation' ? 'primary' : 'inherit'}
                                    onClick={() => handleContentTypeButtonClick('Simulation')}
                                    disabled={!simulationsEnabled}
                                    sx={{ flex: 1 }}
                                >
                                    Simulation
                                </Button>
                            </Box>
                        </Grid>

                        {/* Course Type Selection (Individual / Content Pack) */}
                        {formState.contentType === 'Course' && coursesEnabled && (
                            <Grid item xs={12}>
                                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                    <Typography variant="subtitle2">
                                        Course selection
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                    <Button
                                        variant={courseType === 'Individual' ? 'contained' : 'outlined'}
                                        color={courseType === 'Individual' ? 'warning' : 'inherit'}
                                        onClick={() => handleCourseTypeClick('Individual')}
                                        sx={{ flex: 1, mr: 1 }}
                                    >
                                        Individual
                                    </Button>

                                    {contentPacksEnabled ? (
                                        <Button
                                            variant={courseType === 'ContentPack' ? 'contained' : 'outlined'}
                                            color={courseType === 'ContentPack' ? 'warning' : 'inherit'}
                                            onClick={() => handleCourseTypeClick('ContentPack')}
                                            sx={{ flex: 1, ml: 1 }}
                                        >
                                            Content Pack
                                        </Button>
                                    ) : (
                                        <Box sx={{ flex: 1, ml: 1 }} />
                                    )}
                                </Box>
                            </Grid>
                        )}

                        {/* Individual Course Selection */}
                        {formState.contentType === 'Course' && courseType === 'Individual' && coursesEnabled && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    size="small"
                                    options={courseData || []}
                                    getOptionLabel={(option) => option.courseName}
                                    value={courseData?.find(c => c.courseName === formState.selectedContentName && formState.selectedContentName !== 'Content Pack') || null}
                                    onChange={(event, newValue) => {
                                        const syntheticEvent = {
                                            target: {
                                                name: 'selectedContentName',
                                                value: newValue?.courseName || ''
                                            }
                                        };
                                        handleContentSelection(syntheticEvent);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Course"
                                            required
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.courseName === value?.courseName}
                                />
                            </Grid>
                        )}

                        {/* Content Pack Selection */}
                        {formState.contentType === 'Course' && courseType === 'ContentPack' && contentPacksEnabled && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    size="small"
                                    options={contentPackData || []}
                                    getOptionLabel={(option) => option.contentPackName}
                                    value={contentPackData?.find(p => p.contentPackName === formState.contentName) || null}
                                    onChange={(event, newValue) => {
                                        const syntheticEvent = {
                                            target: {
                                                name: 'selectedContentPackId',
                                                value: newValue?.contentPackName || ''
                                            }
                                        };
                                        handleContentPackSelection(syntheticEvent);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Content Pack"
                                            required
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.contentPackName === value?.contentPackName}
                                />
                            </Grid>
                        )}

                        {/* Simulation Type Selection */}
                        {formState.contentType === 'Simulation' && simulationsEnabled && (
                            <Grid item xs={12}>
                                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                    <Typography variant="subtitle2">
                                        Simulation selection
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                    <Button
                                        variant={simulationType === 'Manual' ? 'contained' : 'outlined'}
                                        color={simulationType === 'Manual' ? 'primary' : 'inherit'}
                                        onClick={() => handleSimulationTypeClick('Manual')}
                                        sx={{ flex: 1, mr: 1 }}
                                    >
                                        Manual
                                    </Button>

                                    {featureSwitchesApiData.enableRandomSimulation ? (
                                        <Button
                                            variant={simulationType === 'Random' ? 'contained' : 'outlined'}
                                            color={simulationType === 'Random' ? 'primary' : 'inherit'}
                                            onClick={() => handleSimulationTypeClick('Random')}
                                            sx={{ flex: 1, ml: 1 }}
                                        >
                                            Random
                                        </Button>
                                    ) : (
                                        <Box sx={{ flex: 1, ml: 1 }} />
                                    )}
                                </Box>
                            </Grid>
                        )}

                        {/* Manual Simulation Selection */}
                        {formState.contentType === 'Simulation' && simulationType === 'Manual' && simulationsEnabled && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    size="small"
                                    options={simulationData || []}
                                    getOptionLabel={(option) => option.simulationName}
                                    value={simulationData?.find(s => s.simulationName === formState.selectedContentName && formState.selectedContentName !== 'Random Simulation') || null}
                                    onChange={(event, newValue) => {
                                        const syntheticEvent = {
                                            target: {
                                                name: 'selectedContentName',
                                                value: newValue?.simulationName || ''
                                            }
                                        };
                                        handleContentSelection(syntheticEvent);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Simulation"
                                            required
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.simulationName === value?.simulationName}
                                />
                            </Grid>
                        )}

                        {/* Random Simulation Collection Selection */}
                        {formState.contentType === 'Simulation' && simulationType === 'Random' && simulationsEnabled && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    size="small"
                                    options={collectionData || []}
                                    getOptionLabel={(option) => option.collectionName}
                                    value={collectionData?.find(c => c.collectionName === formState.contentName) || null}
                                    onChange={(event, newValue) => {
                                        const syntheticEvent = {
                                            target: {
                                                name: 'selectedCollectionId',
                                                value: newValue?.collectionName || ''
                                            }
                                        };
                                        handleCollectionSelection(syntheticEvent);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Collection"
                                            required
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.collectionName === value?.collectionName}
                                />
                            </Grid>
                        )}

                        {/* Delivery Mode Selection */}
                        <Grid item xs={12} sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Delivery mode
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                                <Button
                                    variant={!formState.isBatchSend ? 'contained' : 'outlined'}
                                    color={!formState.isBatchSend ? 'secondary' : 'inherit'}
                                    onClick={() => handleBatchSendChange('isBatchSend', false)}
                                    sx={{ flex: 1 }}
                                >
                                    Direct Send
                                </Button>
                                <Button
                                    variant={formState.isBatchSend ? 'contained' : 'outlined'}
                                    color={formState.isBatchSend ? 'secondary' : 'inherit'}
                                    onClick={() => handleBatchSendChange('isBatchSend', true)}
                                    sx={{ flex: 1 }}
                                >
                                    Batch Send
                                </Button>
                            </Box>
                        </Grid>

                        {/* Start Date Selection */}
                        <Grid item xs={12}>
                            <TextField
                                size="small"
                                name="scheduledFor"
                                label={formState.isBatchSend ? "Start Date" : "Scheduled For"}
                                type="date"
                                value={formState.scheduledFor}
                                onChange={handleFormChange}
                                fullWidth
                                required
                                disabled={true}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        {/* Batch Send Options */}
                        {formState.isBatchSend && (
                            <>
                                <Grid item xs={12}>
                                    <TrimmedTextField
                                        size="small"
                                        label="End Date"
                                        inputRef={endDateRef}
                                        defaultValue={formatDate(formState.scheduledEndDate)}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        required
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <CalendarTodayIcon
                                                        sx={{ cursor: 'pointer' }}
                                                        onClick={() => endDateRef.current?._flatpickr?.open()}
                                                    />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Between the hours
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <TrimmedTextField
                                        size="small"
                                        label="Start Time"
                                        inputRef={startTimeRef}
                                        value={formState.sendingTimeStart || ''}
                                        placeholder="HH:mm"
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        required
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <AccessTimeIcon
                                                        sx={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            const fp = startTimeRef.current?._flatpickr;
                                                            if (fp) {
                                                                if (formState.sendingTimeStart) {
                                                                    fp.setDate(formState.sendingTimeStart);
                                                                }
                                                                fp.open();
                                                            }
                                                        }}
                                                    />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TrimmedTextField
                                        size="small"
                                        label="End Time"
                                        inputRef={endTimeRef}
                                        value={formState.sendingTimeEnd || ''}
                                        placeholder="HH:mm"
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        required
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <AccessTimeIcon
                                                        sx={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            const fp = endTimeRef.current?._flatpickr;
                                                            if (fp) {
                                                                if (formState.sendingTimeEnd) {
                                                                    fp.setDate(formState.sendingTimeEnd);
                                                                }
                                                                fp.open();
                                                            }
                                                        }}
                                                    />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <LoadingSwitch
                                                checked={formState.pauseWeekends || false}
                                                onChange={handleWeekendPauseChange}
                                                loading={isWeekendPauseLoading}
                                            />
                                        }
                                        label="Pause on Weekends"
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button onClick={onClose} variant="contained" color="secondary">
                    Cancel
                </Button>
                <Button
                    onClick={onAdd}
                    variant="contained"
                    color="primary"
                    disabled={
                        noneEnabled ||
                        !formState.contentId ||
                        !formState.scheduledFor ||
                        (formState.isBatchSend && (
                            !formState.scheduledEndDate ||
                            !formState.sendingTimeStart ||
                            !formState.sendingTimeEnd
                        ))
                    }
                >
                    Add Item
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddItemModal;
